/**
 * DeletePhotoDialog - App-styled delete confirmation
 * 
 * Replaces window.confirm() / alert() with proper app UI.
 * Uses the same toast system as the rest of the app.
 */

import { useState } from "react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import { Trash, SpinnerGap, Warning } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function DeletePhotoDialog({
  open,
  onOpenChange,
  photo,
  onDeleteSuccess,
}) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!photo?.photo_id) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API}/photos/${photo.photo_id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Photo deleted");
        onDeleteSuccess?.(photo.photo_id);
        onOpenChange(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete photo");
      }
    } catch (error) {
      toast.error("Failed to delete photo");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-photo-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Warning size={20} className="text-red-500" />
            Delete Photo
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this photo? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-delete-photo"
          >
            {deleting ? (
              <>
                <SpinnerGap size={16} className="mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash size={16} className="mr-2" />
                Delete
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeletePhotoDialog;
